<template>

  <div class="shared-field-wrapper">
     <!-- Header: Label + Undo Action -->
    <div class="d-flex justify-content-between align-items-end mb-1">
       <label class="mb-0 font-weight-bold font-size-sm"
        > <span class="text-dark">{{ label }}</span
        > <span
          v-if="required"
          class="ml-1 text-danger"
          >*</span
        > </label
      >
    </div>
     <!-- Input Area -->
    <div class="position-relative">
       <!-- Select Dropdown (for type='select') --> <b-form-select
        v-if="type === 'select'"
        ref="input"
        v-model="localValue"
        size="sm"
        class="shared-input shared-select"
        :class="inputClasses"
        :options="options"
        :disabled="isDisabled"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-describedby="feedbackId"
        @change="onSelectChange"
      /> <!-- Text Input (default) --> <b-form-input
        v-else
        ref="input"
        :type="type"
        v-model="localValue"
        size="sm"
        class="shared-input"
        :class="inputClasses"
        :placeholder="computedPlaceholder"
        :disabled="isDisabled"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-describedby="feedbackId"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter.prevent="onEnter"
      /> <!-- Search Button (Optional, only for text inputs) --> <button
        v-if="hasSearch && type !== 'select'"
        class="btn-shared-search"
        :class="{ 'is-loading': isLoading }"
        type="button"
        :disabled="isLoading || isDisabled || !localValue"
        @click="$emit('search', localValue)"
      >
         <i
          v-if="isLoading"
          class="fas fa-spinner fa-spin text-primary"
        ></i
        > <i
          v-else
          class="fas fa-search"
        ></i
        > </button
      > <button
        v-if="canClear"
        class="btn-shared-clear"
        type="button"
        :disabled="isDisabled"
        v-b-tooltip.hover
        title="Xóa giá trị"
        @click="onClear"
      >
         <i class="fas fa-times"></i> </button
      > <!-- Status Indicator Icon -->
      <div
        class="input-status-icon"
        :class="{ 'has-search': hasSearch && type !== 'select', 'has-action': canClear }"
      >
         <i
          v-if="isMixed && !isModified"
          class="fas fa-layer-group text-warning"
          v-b-tooltip.hover
          :title="`${fieldState.distinctValues.length} giá trị khác nhau`"
        ></i
        >
      </div>

    </div>
     <!-- Helper / Feedback -->
    <div
      :id="feedbackId"
      class="mt-1 d-flex align-items-center"
      style="min-height: 18px"
      aria-live="polite"
    >
       <transition
        name="fade"
        mode="out-in"
        > <small
          v-if="showError"
          class="text-danger font-size-xs"
          >{{ error }} </small
        > <small
          v-else-if="isMixed && !isModified"
          class="text-muted font-size-xs font-italic"
          > * Các hóa đơn đang có thông tin khác nhau </small
        > </transition
      >
    </div>

  </div>

</template>

<script>
  import { FIELD_ACTION } from '@/view/pages/einvoice/utils/bulkBuyer.utils'

  export default {
    name: 'SharedField',
    props: {
      fieldKey: { type: String, required: true },
      label: { type: String, required: true },
      required: { type: Boolean, default: false },
      placeholder: { type: String, default: '' },
      type: { type: String, default: 'text' },
      fieldState: {
        type: Object,
        default: () => ({
          isMixed: false,
          uniformValue: '',
          distinctValues: [],
        }),
      },
      override: { type: Object, default: () => ({ action: 'KEEP', value: '' }) },
      allowClear: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      hasSearch: { type: Boolean, default: false },
      isLoading: { type: Boolean, default: false },
      options: { type: Array, default: () => [] },
      error: { type: String, default: '' },
      touched: { type: Boolean, default: false },
    },

    computed: {
      showError() {
        return this.touched && this.error
      },

      isModified() {
        return this.override.action !== FIELD_ACTION.KEEP
      },

      canClear() {
        return (
          this.allowClear &&
          !this.isDisabled &&
          (this.isMixed || this.isModified || !!this.localValue)
        )
      },

      isMixed() {
        return this.fieldState.isMixed
      },

      isDisabled() {
        return this.disabled
      },

      feedbackId() {
        return `shared-field-feedback-${this.fieldKey}`
      },

      computedPlaceholder() {
        if (this.isMixed && !this.isModified) {
          return `(Nhập để thay đổi ${this.fieldState.distinctValues.length} giá trị khác nhau)`
        }
        return this.placeholder
      },

      localValue: {
        get() {
          if (this.override.action === FIELD_ACTION.CLEAR) {
            return ''
          }
          if (this.isModified) {
            return this.override.value || ''
          }
          if (this.isMixed) {
            return '' // Empty for mixed state to show placeholder
          }
          return this.fieldState.uniformValue || ''
        },
        set(val) {
          // Trigger override immediately on input
          this.$emit('update', {
            action: FIELD_ACTION.OVERRIDE,
            value: val,
          })
        },
      },

      inputClasses() {
        return {
          'is-mixed': this.isMixed && !this.isModified,
          'is-modified': this.isModified,
          'is-invalid': this.showError,
          'has-clear': this.canClear,
          'bg-white': true,
        }
      },
    },

    methods: {
      undo() {
        this.$emit('update', { action: FIELD_ACTION.KEEP })
        this.$emit('undo')
      },

      onFocus() {
        // Optional: Auto-select text or specific behavior
      },

      onBlur() {
        // Mark field as touched for validation display
        this.$emit('blur')
      },

      onEnter() {
        if (this.hasSearch) {
          this.$emit('search', this.localValue)
        }
      },

      // onClear() {
      //   this.$emit('update', { action: FIELD_ACTION.CLEAR })
      // },
      onClear(val) {
        // Emit update for select change
        this.$emit('update', {
          action: FIELD_ACTION.OVERRIDE,
          value: '',
        })
      },

      onSelectChange(val) {
        // Emit update for select change
        this.$emit('update', {
          action: FIELD_ACTION.OVERRIDE,
          value: val,
        })
      },

      focus() {
        if (this.$refs.input) this.$refs.input.focus()
      },
    },
  }
</script>

<style lang="scss" scoped>
  $primary: #3699ff;
$primary-light: #e1f0ff;
$primary-dark: #2b7de9;
$warning: #ffa800;
$warning-bg: #fff9ed;
$danger: #f64e60;
$danger-bg: #fff5f8;
$text-muted: #9ca3af;
$border-color: #e5e7eb;
$neutral-100: #f4f5f7;
$neutral-700: #374151;
$radius-sm: 6px;

.shared-field-wrapper {
  margin-bottom: 1.125rem;

  .d-flex {
    margin-bottom: 0.375rem;
  }

  label {
    letter-spacing: -0.01em;
  }
}

.shared-input {
  padding-right: 2.5rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid $border-color;
  border-radius: $radius-sm;
  font-weight: 450;
  color: $neutral-700;
  background-color: #fff;

  &::placeholder {
    color: $text-muted;
    font-weight: 400;
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: $primary;
    background-color: #fafbfc;
  }

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.08);
    outline: none;
  }

  // Mixed State Style
  &.is-mixed {
    background-color: $warning-bg !important;
    border-color: #fde68a;

    &::placeholder {
      color: darken($warning, 10%);
      font-style: italic;
    }
  }

  // Modified State Style
  &.is-modified {
    border-color: $primary;
    background-color: #f8fafc !important;
  }

  // Invalid/Error State Style
  &.is-invalid {
    border-color: $danger !important;
    background-color: $danger-bg !important;
    background-image: none !important;

    &:focus {
      border-color: $danger;
      box-shadow: 0 0 0 3px rgba($danger, 0.08);
    }
  }

  // Select-specific styles
  &.shared-select {
    padding-right: 2rem;
  }

  &.has-clear {
    padding-right: 4.5rem;
  }
}

.input-status-icon {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 0.9rem;
  z-index: 5;
  transition: all 0.2s;

  &.has-search {
    right: 2.25rem;
  }

  &.has-action {
    right: 2.5rem;
  }
}

.btn-shared-search {
  position: absolute;
  top: 1px;
  right: 1px;
  height: calc(100% - 2px);
  width: 32px;
  border: none;
  background: $neutral-100;
  border-radius: 0 $radius-sm $radius-sm 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $text-muted;
  transition: all 0.2s;
  z-index: 6;

  &:hover:not(:disabled) {
    background: $primary-light;
    color: $primary-dark;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &.is-loading {
    cursor: wait;
    background: #fff;
  }
}

.btn-shared-clear {
  position: absolute;
  top: 1px;
  right: 1px;
  height: calc(100% - 2px);
  width: 32px;
  border: none;
  background: $neutral-100;
  border-radius: 0 $radius-sm $radius-sm 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $text-muted;
  transition: all 0.2s;
  z-index: 6;

  &:hover:not(:disabled) {
    background: $danger-bg;
    color: $danger;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.btn-undo {
  color: $text-muted;
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
  font-weight: 450;
  letter-spacing: -0.01em;

  &:hover {
    color: $primary-dark;
    text-decoration: underline;
  }
}

// Vue Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
